import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faClipboardList,
  faLocationDot,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import Button from '../components/Button';
import LocationSetup from '../components/LocationSetup';
import AdoptablePets from '../components/AdoptablePets';
import QuizResponsesDisplay from '../components/QuizResponsesDisplay';
import { usePetSearch } from '../hooks/usePetSearch';
import { hasCompletedQuiz, loadQuizResponses } from '../utils/quizStorage';

function PetSearch() {
  const [showLocationSetup, setShowLocationSetup] = useState(false);
  const [breedPreference, setBreedPreference] = useState(null);
  const locationState = useLocation();
  const hasQuizData = hasCompletedQuiz();
  const quizResponses = hasQuizData ? loadQuizResponses() : null;
  
  // Check if we came from breed recommendations with a specific breed
  useEffect(() => {
    if (locationState.state?.breedPreference) {
      setBreedPreference({
        breed: locationState.state.breedPreference,
        animalType: locationState.state.animalType
      });
    }
  }, [locationState.state]);
  
  const {
    pets,
    loading,
    error,
    location,
    hasSearched,
    updateLocation,
    refreshSearch,
  } = usePetSearch(quizResponses, breedPreference);

  const handleLocationSaved = (newLocation) => {
    updateLocation(newLocation);
    setShowLocationSetup(false);
  };

  // If no quiz data, show prompt to take quiz first
  if (!hasQuizData) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
            <FontAwesomeIcon icon={faClipboardList} className="h-16 w-16 text-primary-500 mb-6" />
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">
              Take the Quiz First
            </h1>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 mb-6">
              To find pets that match your lifestyle and preferences, you need to complete our compatibility quiz first.
            </p>
            
            <div className="bg-primary-50 dark:bg-primary-900 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-primary-900 dark:text-primary-100 mb-3">
                The quiz helps us find pets that match:
              </h2>
              <ul className="text-left text-primary-700 dark:text-primary-200 space-y-2">
                <li>• Your experience level with pets</li>
                <li>• Your living situation and available space</li>
                <li>• How much time you can dedicate to pet care</li>
                <li>• Your preferred pet size and energy level</li>
              </ul>
            </div>
            
            <Link to="/quiz">
              <Button variant="primary" size="large">
                <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                Take the Compatibility Quiz
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <FontAwesomeIcon icon={faSearch} className="h-8 w-8 text-primary-500 mr-3" />
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
              Find Adoptable Pets
            </h1>
          </div>
          <p className="text-lg text-neutral-600 dark:text-neutral-300">
            {breedPreference 
              ? `Searching for adoptable ${breedPreference.breed}s in your area`
              : 'Discover pets near you that match your preferences'
            }
          </p>
        </div>

        {/* Breed Preference Banner */}
        {breedPreference && (
          <div className="mb-6 bg-gradient-to-r from-primary-500 to-blue-600 dark:from-primary-600 dark:to-primary-800 rounded-xl p-4">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faSearch} className="h-5 w-5 mr-3" />
                <span className="font-medium">
                  Searching for: <strong>{breedPreference.breed}</strong> ({breedPreference.animalType}s)
                </span>
              </div>
              <Button 
                variant="outline" 
                size="small"
                onClick={() => setBreedPreference(null)}
                className="bg-white bg-opacity-20 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-30"
              >
                Clear Filter
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar: Quiz Results and Location */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quiz Responses Summary */}
            <QuizResponsesDisplay showHeader={false} compact={true} />
            
            {/* Location Setup/Display */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  Search Location
                </h2>
                <Button 
                  variant="outline" 
                  size="small"
                  onClick={() => setShowLocationSetup(!showLocationSetup)}
                >
                  <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                  {location ? 'Change' : 'Set Location'}
                </Button>
              </div>

              {location ? (
                <div className="space-y-3">
                  <div className="bg-primary-50 dark:bg-primary-900 rounded-lg p-3">
                    <p className="text-primary-700 dark:text-primary-200 text-sm">
                      <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                      {location.city && `${location.city}, `}
                      {location.state && `${location.state} `}
                      {location.zipCode}
                    </p>
                    <p className="text-primary-600 dark:text-primary-300 text-xs mt-1">
                      Within {location.radius} miles
                    </p>
                  </div>
                  
                  {hasSearched && (
                    <Button variant="secondary" size="small" onClick={refreshSearch} className="w-full">
                      Refresh Search
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faLocationDot} className="h-8 w-8 text-neutral-400 mb-2" />
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                    Set your location to start searching for pets
                  </p>
                </div>
              )}

              {showLocationSetup && (
                <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <LocationSetup 
                    onLocationSaved={handleLocationSaved}
                    initialLocation={location}
                    showTitle={false}
                  />
                </div>
              )}
            </div>

            {/* Back to Profile Link */}
            <div className="text-center">
              <Link to="/profile">
                <Button variant="ghost" size="small">
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  Back to Profile
                </Button>
              </Link>
            </div>
          </div>

          {/* Main Content: Pet Results */}
          <div className="lg:col-span-2">
            <AdoptablePets
              pets={pets}
              loading={loading}
              error={error}
              onRefresh={refreshSearch}
              userLocation={location}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PetSearch;